/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ContentInjectionService } from './contentInjection.service';
import { EventEmitter, Injectable } from '@angular/core';
import { LogService } from './log.service';
import { NotificationEnum, TokenStatus } from '../../enums';
import { NotifyService } from './notify.service';
import { Observable } from 'rxjs';
import { UserService } from './user.service';
import { UserSessionModel } from '../../models';
import { environment, ModulesEnum } from '../../../../environments/environment';
import { filter, share } from 'rxjs/operators';

/**
 * Socket Service built on top of the WebSocket API. A lot of the actions resemble socket.io for ease of use.
 *
 * Use socket.emit() to send information using an "eventName" to tag the sort of request
 * that is being sent to the server.
 * Use socket.on() to listen in on a response from a server by a specific "eventName".
 *
 * WebSocket Connection States
 * 1) OPEN
 * 2) CLOSED
 * 3) CLOSING
 * 4) CONNECTING
 */
@Injectable()
export class SocketService {
    /**
     * Socket close emitter.
     */
    public socketClosedEmitter: EventEmitter<any> = new EventEmitter();

    /**
     *  WebSocket ready state. This is based of the WS API.
     * @private
     */
    private readyState: number;

    /**
     * Manual socket close.
     * @private
     */
    private forcedClose: boolean = false;

    /**
     * Websocket variable.
     * @private
     */
    private ws: WebSocket;

    /**
     * Holds pending event.
     * @private
     */
    private eventQueue: any[] = [];

    /**
     * Websocket event emitter.
     * @private
     */
    private listener: EventEmitter<any> = new EventEmitter();

    /**
     *  Total number of on subscribers.
     *  @private
     */
    private totalSubscribers: number = 0;

    /**
     * Store if content overlay is open.
     * @private
     */
    private isContentOverlayOpen: boolean = false;

    /**
     * Store events that aren't emitted when the content overlay is open.
     * @private
     */
    private storedEventQueue: any = [];

    /**
     * Store the websocket timeout.
     * @private
     */
    private wsErrorTimeout: any;

    constructor(
        private contentInjectionService: ContentInjectionService,
        private logService: LogService,
        private notifyService: NotifyService,
        private userService: UserService,
    ) {
        this.subscribeToUserService();
        this.subscribeToInjectionService();
    }

    /**
     * Create a socket Connection and it's listeners.
     */
    public connect(): void {
        const user: UserSessionModel = this.userService.getUser();

        if (this.missingParameters(user)) {
            return;
        }

        const { newUrl, protocols } = this.getWebSocketConfig(user);

        this.ws = new WebSocket(newUrl, protocols);
        this.ws.onopen = (): void => this.onOpen(user);
        this.ws.onclose = (): void => this.onClose();
        this.ws.onmessage = (event: MessageEvent): void => this.onMessage(event);
        this.ws.onerror = (event: Event): void => this.onError(event);
    }

    /**
     * Listen for a specific on message by type e.g. 'stats'.
     * @param eventName
     */

    public on(eventName?: string): Observable<any> {
        // Count total number of subscribers we have used throughout the application
        this.totalSubscribers++;

        // Log total number of subscribers
        this.logService.debug('SocketService:onMessage()', 'Total subscribers: ' + this.totalSubscribers);

        return this.listener.pipe(
            filter((data: any): boolean => this.processData(data, eventName)),
            share()
        );
    }

    /**
     * Function used to emit or send information via a websocket.
     * @param service - GSLB - systemStats
     * @param event
     */
    public emit(service: string, event: string): void {
        // Do we have a socket variable?
        if (this.ws && service?.length > 0) {
            const send = {
                service,
                'version': 'v1',
                event
            };

            // What is our ready state?
            if (this.readyState === WebSocket.OPEN) {
                this.logService.debug('SocketService:emit()', 'Sending event: ' + service);

                return this.ws.send(JSON.stringify(send));
            } else {
                this.logService.debug('SocketService:emit()', 'Socket connection is not OPEN. Event queued!');
                // Check to see if the request has already been queue before
                if (!this.eventQueueItemExists(service)) {
                    const newRequest: any = { eventName: service, data: event };

                    // Add event to the queue
                    this.eventQueue.push(newRequest);

                    return;
                } else {
                    this.logService.debug('SocketService:emit()', 'Socket event already queued.. skipping!');

                    return;
                }
            }
        } else {
            this.logService.error('SocketService:emit()', 'WebSocket variable not setup or parameter "eventName" is empty.');

            return;
        }
    }

    /**
     * Force close a socket connection.
     */
    public close(): void {
        // Let our listener know we are closing the connection
        this.forcedClose = true;
        // Close our connection
        this.ws.close(1000, 'Application initialised closure');
    }

    /**
     * Process data
     * @param data
     * @param eventName
     * @private
     */
    private processData(data: any, eventName: string): any {
        if (!this.isContentOverlayOpen) { // This stops action dots from becoming detached from their source.
            return eventName ? data.eventName?.toLowerCase() === eventName.toLowerCase() : data;
        } else {
            this.storedEventQueue.push(data);
            if (this.storedEventQueue.length >= 10) {
                this.storedEventQueue.shift();
            }

            return false;
        }
    }

    /**
     * Check for missing parameters
     * @param user
     * @private
     */
    private missingParameters(user: UserSessionModel): boolean {
        if (this.userService.tokenStatus() === TokenStatus.EXPIRED) {
            this.userService.logout();

            return;
        }

        // Make sure we are not already connected
        if (this.readyState === WebSocket.OPEN) {
            return;
        }

        if (!user.applianceAddress) {
            this.logService.error('socket:connect()',
                'Missing connection details', { ip: user.applianceAddress ? user.applianceAddress : '' }
            );

            return;
        }
    }

    /**
     * Get websocket config.
     * @param user
     * @private
     */
    private getWebSocketConfig(user: UserSessionModel): { newUrl: string, protocols: string[] } {
        const newUrl: string = 'wss://' + user.applianceAddress + ':' + user.appliancePort + '/ws';
        this.logService.debug('SocketService:connect()', 'Connecting to ' + user.applianceAddress);

        const protocols: string[] = environment.module === ModulesEnum.CENTRALISED_MANAGEMENT
            ? []
            : ['lbProtocol', 'Bearer-' + user.JWT];

        return { newUrl, protocols };
    }

    /**
     *On open.
     * @param user
     * @private
     */
    private onOpen(user: UserSessionModel): void {
        this.logService.debug('SocketService:onOpen()', 'Socket Connected');
        this.readyState = WebSocket.OPEN;
        this.authConnection(user.JWT); /// TODO ?
        this.processEventQueue();
    }

    /**
     * On close.
     * @private
     */
    private onClose(): void {
        // Clear out or websocket variable
        this.ws = null;

        // Are we manual closing the connection?
        if (this.forcedClose) {
            this.logService.debug('SocketService:onClose()', 'Socket closed.');
            this.readyState = WebSocket.CLOSED;
        } else {
            this.logService.debug('SocketService:onClose()', 'Socket closed unexpectedly.');

            // Websocket has disconnected without our permission. Reconnect!
            this.readyState = WebSocket.CONNECTING;

            this.logService.debug('SocketService:onClose()', 'Reconnecting..');

            // We need to reconnect.
            this.attemptReconnect();
        }

        this.socketClosedEmitter.emit(true);
    }

    /**
     * On message.
     * @param event
     * @private
     */
    private onMessage(event: any): void {
        const jsonP = JSON.parse(event.data);
        this.socketClosedEmitter.emit(false);
        this.listener.emit(jsonP);
    }

    /**
     * On error.
     * @param event
     * @private
     */
    private onError(event: any): void {
        this.logService.error('SocketService:onError()', 'Websocket error', event);
        this.listener.emit(event.type);
    }

    /**
     * Check to see if an event already exists in the event queue.
     * If the data is different, it will consider it as a new event.
     * @param eventName
     * @private
     */
    private eventQueueItemExists(eventName: string): boolean {
        for (const i in this.eventQueue) {
            if (this.eventQueue[i]) {
                const event = this.eventQueue[i];

                if (event.eventName === eventName) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Iterate through pending requests.
     * @private
     */
    private processEventQueue(): void {
        // Log how many are in queue
        if (this.eventQueue?.length > 0) {
            for (const i in this.eventQueue) {
                if (this.eventQueue[i]) {
                    const event = this.eventQueue[i];

                    // Debug how many we have in queue
                    this.logService.debug('SocketService:processEventQueue()', 'Total: ' + this.eventQueue.length);

                    // Send the event
                    this.emit(event.eventName, event.data);
                }
            }
            // We are done clear our event queue
            this.eventQueue = [];
        } else {
            this.logService.debug('SocketService:processEventQueue()', 'Socket event queue is empty. Nothing to process!');
        }
    }

    /**
     * Attempt to re-establish our socket connection.
     * @private
     */
    private attemptReconnect(): void {
        this.wsErrorTimeout = setTimeout(() => {
            this.setWebsocketErrorNotification();
        }, 5000);

        const reconnectTimer = setTimeout((): void => {
            if (this.readyState === WebSocket.OPEN) {
                this.notifyService.removeNotificationById('websocket-issue');
                clearTimeout(this.wsErrorTimeout);
                clearTimeout(reconnectTimer);
            }

            this.connect();

        }, 5000);
    }

    /**
     * Set web socket error.
     * @private
     */
    private setWebsocketErrorNotification(): void {
        const { message, url, translate } = environment.module === ModulesEnum.CENTRALISED_MANAGEMENT
            ? this.getPortalNotification()
            : this.getEnduranceNotification();

        this.notifyService.error(
            message,
            {
                id: 'websocket-issue',
                level: NotificationEnum.ERROR,
                static: true,
                url,
                messageTranslate: translate,
                waiting: !url
            }
        );
    }

    /**
     * Endurance notification parameters.
     * @private
     */
    private getEnduranceNotification(): any {
        const message = `
            Unable to establish a connection with the Bear.
            <br>
            <small>
                Click here to visit the services page and double-check the bear is running.
            </small>
        `;
        const translate: boolean = false;
        const url = 'maintenance/services?sFilter=bear';

        return { message, url, translate };
    }

    /**
     * Portal notification parameters.
     * @private
     */
    private getPortalNotification(): any {
        const message = 'socket_service_reconnect_message';
        const translate: boolean = true;
        const url = null;

        return { message, url, translate };
    }

    /**
     * Subscribe to user service to detect logout.
     * @private
     */
    private subscribeToUserService(): void {
        this.userService.logOutEmitter
            .subscribe({
                next: (state: boolean): void => {
                    if (state) {
                        this.storedEventQueue = [];
                        this.close();
                    }
                }
            });
    }

    /**
     * Subscribe to injection service so we can't stop emitting when the service is open.
     * This stops the table being rewritten while the action dots are open.
     * @private
     */
    private subscribeToInjectionService(): void {
        this.contentInjectionService.isOpen
            .subscribe({
                next: (event: any): void => {
                    if (event.id === 'action-dots') {
                        if (event.open) {
                            this.logService.debug('SocketService:processEventQueue()', 'Pausing socket data');
                            this.isContentOverlayOpen = true;
                        }
                    }

                    if (!event.open && this.storedEventQueue.length) {
                        this.logService.debug(
                            'SocketService:processEventQueue()',
                            `Processing socket queue of ${this.storedEventQueue.length} items`
                        );
                        this.isContentOverlayOpen = false;
                        this.processStoredQueue();
                    }
                }
            });
    }

    /**
     * Process the store queue which builds up when the injection service is open.
     * @private
     */
    private processStoredQueue(): void {
        let index: any = this.storedEventQueue.length;
        while (index--) {
            this.listener.next(this.storedEventQueue[index]);
            this.storedEventQueue.splice(index, 1);
        }
    }

    /**
     * Auth the connection for the ws
     * @param jwt
     * @private
     */
    private authConnection(jwt: string): void {
        // Do we have a socket variable?
        if (this.ws) {
            // What is our ready state?
            if (this.readyState === WebSocket.OPEN) {
                this.logService.debug('SocketService:emit()', 'Sending event: ' + 'auth');

                return this.ws.send(JSON.stringify({ 'eventName': 'auth', 'data': { 'jwt': jwt } }));
            }
        }
    }
}
